import { ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Switch } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { ChartTitleAndDescription } from 'src/components/molecules';
import { getColumnNames } from 'src/components/pages/EthicsOperationsDataview/helpers';
import { UploadScreen } from 'src/components/pages/UploadUsers';
import { BulkReinviteCreateUser, BulkReinviteExistingUser } from 'src/graphql';
import { FeatureFlag, State } from 'src/types';
import { sortDataArrayByDate, sortDataArrayByString } from 'src/utils';
import {
  Bold,
  Description,
  DescriptionContainer,
  Left,
  List,
  ListItem,
  Right,
  SwitchContainer,
  TableInfo,
} from './UploadActions.styles';

const columns = [
  { label: 'Full name' },
  { label: 'Email' },
  { label: 'Role' },
  { label: 'Department' },
  { label: 'Site' },
  { label: 'Country' },
  { label: 'Employee ID' },
  { label: 'Termination Date' },
];

export const useUploadActions = (
  screen: UploadScreen,
  usersToCreate: BulkReinviteCreateUser[],
  usersToReinvite: BulkReinviteExistingUser[],
  usersToDeactivate: BulkReinviteExistingUser[],
) => {
  const screenIsActivate = screen === UploadScreen.ACTIVATE;

  const numberOfUsersToInvite = usersToCreate.length + usersToReinvite.length;
  const usersToInviteText = `There
  ${numberOfUsersToInvite === 1 ? 'is' : 'are'}
  ${numberOfUsersToInvite}
  ${numberOfUsersToInvite === 1 ? 'employee' : 'employees'}
  ready to be activated`;

  const usersToCreateText = ` new ${
    usersToCreate.length === 1 ? 'employee' : 'employees'
  } will be activated`;

  const usersToReinviteText = ` deactivated ${
    usersToReinvite.length === 1 ? 'employee' : 'employees'
  } will be re-activated`;

  const usersToDeactivateText = `There ${
    usersToDeactivate.length === 1 ? 'is' : 'are'
  } ${usersToDeactivate.length}   ${
    usersToDeactivate.length === 1 ? 'employee' : 'employees'
  } not found in the CSV`;

  const descriptionText = (
    <DescriptionContainer>
      <Description>
        However, they exist in the Resolution hub&apos;s &apos;Manage User
        table&apos;.
      </Description>
      <Description>
        Would you like to flag these employees for deactivation?
      </Description>
    </DescriptionContainer>
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [activateToggleOn, setActivateToggleOn] = useState(true);
  const [deactivateToggleOn, setDeactivateToggleOn] = useState(false);

  const features = useSelector((state: State) => state.features);

  const activateWithoutInvite = isRequiredFeatureEnabled(
    features,
    FeatureFlag.BULK_ACTIVATE_WITHOUT_INVITE,
  );

  const tableUsers = screenIsActivate
    ? [...usersToCreate, ...usersToReinvite]
    : [...usersToDeactivate];

  const [sortedColumn, setSortedColumn] = useState<{
    columnName: string;
    direction: 'asc' | 'desc';
  }>({
    columnName: 'Full name',
    direction: 'asc',
  });

  const handleColumnSort = (columnName: string) => {
    const newSortDirection =
      sortedColumn.columnName === columnName
        ? sortedColumn.direction === 'asc'
          ? 'desc'
          : 'asc'
        : 'asc';
    setSortedColumn({ columnName, direction: newSortDirection });
  };

  const mappedTableUsers = tableUsers.map((c) => ({
    cellData: [
      c.name,
      c.email,
      c.role,
      c.department,
      c.site,
      c.country,
      c.eid,
      c.terminationDate,
    ],
    row: c.name as string,
  }));
  const sortedTableUsers: {
    cellData: (ReactNode | string | number)[];
    row: string;
  }[] = useMemo(() => {
    const columnIndex = getColumnNames(columns).indexOf(
      sortedColumn.columnName,
    );
    const sortDirection = sortedColumn.direction;
    switch (sortedColumn.columnName) {
      case 'Termination Date': {
        const newTableData = sortDataArrayByDate(
          mappedTableUsers,
          columnIndex,
          sortDirection,
        );
        return newTableData.map((n) => ({
          cellData: n.cellData,
          row: n.cellData[0] as string,
        }));
      }
      case 'Full name':
      case 'Email':
      case 'Role':
      case 'Department':
      case 'Site':
      case 'Country': {
        const newTableData = sortDataArrayByString(
          mappedTableUsers,
          columnIndex,
          sortDirection,
        );
        return newTableData.map((n) => ({
          cellData: n.cellData,
          row: n.cellData[0] as string,
        }));
      }
      default:
        return mappedTableUsers;
    }
  }, [sortedColumn.columnName, sortedColumn.direction, mappedTableUsers]);

  const ActivateTableInfo = (
    <TableInfo>
      <Left>
        <ChartTitleAndDescription
          chartTitle={usersToInviteText}
          chartTitleDescription={
            <List>
              {usersToCreate.length > 0 && (
                <ListItem>
                  <Bold>{usersToCreate.length}</Bold>
                  {usersToCreateText}
                </ListItem>
              )}
              {usersToReinvite.length > 0 && (
                <ListItem>
                  <Bold>{usersToReinvite.length}</Bold>
                  {usersToReinviteText}
                </ListItem>
              )}
            </List>
          }
        />
      </Left>
      <Right>
        <Button variant="tertiary" onClick={() => setShowActivationModal(true)}>
          What does activation mean?
        </Button>
        <SwitchContainer>
          <Switch
            label={
              activateWithoutInvite
                ? 'Activate users'
                : 'Send invites and activate users'
            }
            onClick={setActivateToggleOn}
            checked={activateToggleOn}
            data-testid="activate-toggle"
          />
        </SwitchContainer>
      </Right>
    </TableInfo>
  );

  const DeactivateTableInfo = (
    <TableInfo>
      <Left>
        <ChartTitleAndDescription
          chartTitle={usersToDeactivateText}
          chartTitleDescription={descriptionText}
          data-testid="table-title"
        />
      </Left>
      <Right>
        <Button
          variant="tertiary"
          onClick={() => setShowDeactivationModal(true)}
          data-testid="deactivate-modal-button"
        >
          What does deactivation mean?
        </Button>
        <SwitchContainer>
          <Switch
            label="Remove users and deactivate them"
            onClick={setDeactivateToggleOn}
            checked={deactivateToggleOn}
            data-testid="deactivate-toggle"
          />
        </SwitchContainer>
      </Right>
    </TableInfo>
  );

  return {
    screenIsActivate,
    showConfirmationModal,
    setShowConfirmationModal,
    showActivationModal,
    setShowActivationModal,
    showDeactivationModal,
    setShowDeactivationModal,
    activateToggleOn,
    deactivateToggleOn,
    ActivateTableInfo,
    DeactivateTableInfo,
    sortedTableUsers,
    numberOfUsersToInvite,
    columns,
    handleColumnSort,
    sortedColumn,
    activateWithoutInvite,
  };
};
